// src/pages/users.ts —— 06 C-03 用户管理 / C-04 用户 360 抽屉 / C-07 用户资金流水（PAGES_USERS 经 pages/index 注册，后序覆盖 PAGES_SHELLS 同名搭壳页）。
// 加载环 shells → users → cards / funds → shells：本文件顶层只构造视图定义，cards / funds 的导出只在函数体内引用（pack-order 守卫）。
// 会话纪律：每个 await 之后写宿主状态前先过 sessionGuard；360 抽屉会话（Sess）随 session:lost / ready 与抽屉关闭整体丢弃并 revoke blob URL
import { ApiError } from '../../../shared/src/api/client';
import type { RouteKey } from '../../../shared/src/api/routes';
import type { Dict } from '../../../shared/src/dc';
import { interpolate } from '../../../shared/src/errors';
import { fmtAmount, fmtCommission, fmtOriginal, toMicro } from '../../../shared/src/money';
import { fmtDateTime } from '../../../shared/src/time';
import { closeDrawer, openDrawer, openForm, reloadCurrent, sessionGuard } from '../domains/table';
import { str, strArr, type AdminCtx } from '../domains/types';
import { I18N_USERS, REWRITES_USERS } from '../i18n/users';
import { can } from '../perm';
import { openCardDrawer } from './cards';
import { adjustForm } from './funds';
import { openOperationDrawer } from './ops';
import { chipMap, defineView, type ActionDef, type DrawerAction, type DrawerSpec, type ErasedView, type FieldDef, type FormDef, type FormVals, type Need, type PageMap, type StatVals } from './types';
import { cell, chipOf, localErr, type CellVals, type ChipKind, type Tone } from './ui';
import type {
  AdminUserRow,
  Card360,
  Commissions360,
  Fund360,
  KycAdminView,
  KycLatest,
  List360,
  Log360Page,
  Log360Row,
  Overview360,
  Spend360,
  U360Tab,
  WalletLogRow,
  WalletLogStats,
} from './users.types';

const U_EDIT: Need = { module: 'users', bit: 'edit' };
const U_APPROVE: Need = { module: 'users', bit: 'approve' };
const NOTE_MAX = 255;
const BLOB_WATCH_MS = 500; // 抽屉关闭探测周期（DrawerSpec 无关闭回调；仅在本会话持有 blob URL 时运行）
const EMPTY = '—';
// 360 消费页签外币消费原币副标题（「 · 176,000 VND」）：billing_amount 已是卡币种金额（2026-10-19）
const origSub = (x: { original_amount?: string | null; original_currency?: string | null }): string => {
  const o = fmtOriginal(x.original_amount, x.original_currency);
  return o ? ` · ${o}` : '';
};
const RED = ';color:#d1434b';
const GREY = ';color:#8a958f';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TG_ID_RE = /^\d{1,19}$/;

const USER_TABS = ['all', 'kyc_pending', 'verified', 'banned'] as const;
const WALLET_TABS = ['all', 'deposit', 'card', 'withdraw', 'commission', 'manual'] as const;
const TABS_360: readonly U360Tab[] = ['overview', 'cards', 'funds', 'spend', 'commissions', 'log'];

const tz = (ctx: AdminCtx): string | undefined => ctx.s.sh.timezone || undefined;
const textOr = (t: Dict, key: string, fallback: string): string => str(t, key) || fallback;
const pascal = (s: string): string => s.split('_').map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join('');

// ---- 金额（D8-12 BigInt 截断两位；后端值非法时原样显示，不抛错） ----

function micro(v: string | null | undefined): bigint | null {
  if (typeof v !== 'string') return null;
  try {
    return toMicro(v);
  } catch {
    return null;
  }
}

function amt(v: string): string {
  const m = micro(v);
  return m === null ? v : fmtAmount(m, 2);
}

const usd = (v: string): string => `$${amt(v)}`;
// 佣金「{金额} USDT」去尾零（对客评审 2026-10-15 #26：钱包余额与佣金为 USDT，卡相关为 $）；非法值原样
const usdt = (v: string): string => fmtCommission(v, { fallback: v });

// 有符号金额：正数补「+」（原型 L728），负数由 fmtAmount 带「-」
function signed(v: string): string {
  const m = micro(v);
  return m !== null && m > 0n ? `+${amt(v)}` : amt(v);
}

const red = (c: CellVals): CellVals => ({ ...c, style: `${c.style}${RED}` });
const grey = (c: CellVals): CellVals => ({ ...c, style: `${c.style}${GREY}` });

// ---- 状态章映射（06 C-03 / C-07；未映射值 gray 章 + 原值，X-34） ----

const KYC_STATUS: Readonly<Record<string, readonly [ChipKind, string]>> = {
  none: ['gray', 'stKycNone'], pending: ['amber', 'stPending'], passed: ['green', 'stPass'], rejected: ['red', 'stReject'],
};
const USER_STATUS: Readonly<Record<string, readonly [ChipKind, string]>> = { active: ['green', 'stActive'], banned: ['red', 'stBanned'], merged: ['gray', 'stMerged'] };
const LEDGER_STATUS: Readonly<Record<string, readonly [ChipKind, string]>> = { done: ['green', 'stDone'], pending: ['amber', 'stLedgerPending'], cancel: ['gray', 'stCancel'] };
// 06 C-07 类型列：前六类沿用原型页签文案（tabWallets[i]），其余四类为 06 新增键
const KIND_TAB: Readonly<Record<string, readonly [ChipKind, number]>> = {
  deposit: ['green', 1], card_open: ['purple', 2], card_recharge: ['purple', 2], withdraw: ['amber', 3], commission_in: ['green', 4], manual: ['red', 5],
};
const KIND_KEY: Readonly<Record<string, readonly [ChipKind, string]>> = {
  card_withdraw: ['purple', 'lkCardWithdraw'], card_close_refund: ['purple', 'lkCloseRefund'], fee: ['amber', 'lkFee'], revert: ['gray', 'lkRevert'],
};
// 06 C-06 状态 8 态 PascalCase 文案（360 消费页签同口径）
const TX_STATUS_KEY: Readonly<Record<string, string>> = {
  Settle: 'stSettle', Authorize: 'stAuth', AuthorizeDeclined: 'stDeclined', Refund: 'stRefund', Dispute: 'stDispute', Auth_Delete: 'stAuthDel', RefundCancell: 'stRefundCancel', Unknown: 'stTxUnknown',
};
// 基线 §11.2 流水标题模板 → 文案键（与后端 adminuser.RenderTitle 同表）
const TITLE_KEY: Readonly<Record<string, string>> = {
  deposit: 'usrLtDeposit', deposit_fee: 'usrLtDepositFee', withdraw: 'usrLtWithdraw', card_open: 'usrLtCardOpen', card_recharge: 'usrLtCardRecharge', card_withdraw: 'usrLtCardWithdraw',
  card_close_refund: 'usrLtCardCloseRefund', commission_in: 'usrLtCommissionIn', manual: 'usrLtManual', fee: 'usrLtFee', fee_owed_settle: 'usrLtFeeOwedSettle', revert: 'usrLtRevert',
  deposit_reorg_hold: 'usrLtDepositReorgHold', withdraw_activation_fee_refund: 'usrLtActRefund',
};

function kindMap(t: Dict): Readonly<Record<string, readonly [ChipKind, string]>> {
  const tabs = strArr(t, 'tabWallets');
  const out: Record<string, readonly [ChipKind, string]> = {};
  for (const [k, [kind, i]] of Object.entries(KIND_TAB)) out[k] = [kind, tabs[i] ?? k];
  for (const [k, [kind, key]] of Object.entries(KIND_KEY)) out[k] = [kind, textOr(t, key, k)];
  return out;
}

function labelFrom(map: Readonly<Record<string, readonly [ChipKind, string]>>): (t: Dict, v: string) => string {
  return (t, v) => {
    const hit = map[v];
    return (hit && str(t, hit[1])) || v;
  };
}
const kycLabel = labelFrom(KYC_STATUS);
const userStatusLabel = labelFrom(USER_STATUS);
const ledgerStatusLabel = labelFrom(LEDGER_STATUS);
const kindLabel = (t: Dict, v: string): string => kindMap(t)[v]?.[1] ?? v;

function scalar(v: unknown): string | null {
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return null;
}

// title_tpl + title_params 按当前语言渲染；未登记模板或缺参数时显示模板原文（与 CSV 标题列同口径）
export function ledgerTitle(t: Dict, tpl: string, params: Readonly<Record<string, unknown>> | null): string {
  const key = TITLE_KEY[tpl];
  const pat = key ? str(t, key) : '';
  if (!pat) return tpl;
  let failed = false;
  const out = pat.replace(/\{([a-z0-9_]+)\}/g, (m, k: string) => {
    const v = scalar(params?.[k]);
    if (v === null) {
      failed = true;
      return m;
    }
    return v;
  });
  return failed ? tpl : out;
}

const agentText = (a: AdminUserRow['agent']): string => (a ? `${a.biz_no} · ${a.name}` : EMPTY);
const tgName = (u: { telegram_username: string | null }): string => (u.telegram_username ? `@${u.telegram_username}` : '');

// ---- 写路径在途锁（宿主 × 动作键；06 §0.3-6，参照 pages/cards guarded） ----

const inflight = new WeakMap<object, Set<string>>();

function guarded(key: string, fn: (ctx: AdminCtx) => Promise<void>): (ctx: AdminCtx) => Promise<void> {
  return async (ctx) => {
    let keys = inflight.get(ctx.host);
    if (!keys) {
      keys = new Set();
      inflight.set(ctx.host, keys);
    }
    if (keys.has(key)) return;
    keys.add(key);
    const alive = sessionGuard(ctx);
    try {
      await fn(ctx);
    } catch (e) {
      if (alive()) ctx.gp.ui.toast(ctx.gp.ui.err(e));
    } finally {
      keys.delete(key);
    }
  };
}

// ---- C-04 用户 360 抽屉会话 ----

interface LogState {
  rows: readonly Log360Row[];
  total: number;
  page: number;
}

// 一次抽屉会话（open360 → 关闭 / 换开另一抽屉 / 会话结束）：页签数据按需拉取并缓存，KYC blob URL 复用
interface Sess {
  readonly host: object;
  readonly userId: number;
  readonly alive: () => boolean;
  ov: Overview360 | null;
  kyc: KycLatest | null;
  cards: readonly Card360[] | null;
  funds: readonly Fund360[] | null;
  spend: readonly Spend360[] | null;
  comm: Commissions360 | null;
  logs: LogState | null;
  moreBusy: boolean;
  active: U360Tab;
  spec: DrawerSpec | null; // 本会话最近一次放入 tb.drawer 的对象（对象同一性判定）
  readonly blobs: Map<string, string>;
  watch: ReturnType<typeof setInterval> | null;
}

const sessions = new WeakMap<object, Sess>();
const openSeq = new WeakMap<object, number>();
const hookedBuses = new WeakSet<object>();
let logOpener: ((ctx: AdminCtx, opId: number) => void) | null = null;

// C-23 操作详情抽屉注入点（T-D0）；未注入时操作记录行不可点
export function set360LogOpener(fn: ((ctx: AdminCtx, opId: number) => void) | null): void {
  logOpener = fn;
}
// 06 C-04 操作记录行展开 → C-23 操作详情；函数声明在模块实例化期即绑定，users ↔ ops ↔ shells 加载环下顶层引用安全
set360LogOpener(openOperationDrawer);

function dispose(sess: Sess | undefined): void {
  if (!sess) return;
  if (sess.watch !== null) clearInterval(sess.watch);
  sess.watch = null;
  for (const url of sess.blobs.values()) URL.revokeObjectURL(url);
  sess.blobs.clear();
  if (sessions.get(sess.host) === sess) sessions.delete(sess.host);
}

// session:lost / ready 时丢弃 360 会话（revoke blob URL），在途结果由 sessionGuard 丢弃
function hookSession(ctx: AdminCtx): void {
  if (hookedBuses.has(ctx.events)) return;
  hookedBuses.add(ctx.events);
  const host = ctx.host;
  ctx.events.on('session:lost', () => dispose(sessions.get(host)));
  ctx.events.on('session:ready', () => dispose(sessions.get(host)));
}

// 抽屉关闭 / 被其他抽屉替换即结束抽屉会话（06 C-04 偏差 §6-P1：blob URL 同会话复用、关闭即 revoke）
function watchClose(ctx: AdminCtx, sess: Sess): void {
  if (sess.watch !== null) return;
  sess.watch = setInterval(() => {
    if (sessions.get(ctx.host) !== sess || ctx.host.state.tb.drawer !== sess.spec) dispose(sess);
  }, BLOB_WATCH_MS);
}

const R360: RouteKey = 'GET /api/admin/users/{id}/360';

// 只写本会话缓存（会话结束即整体丢弃）；宿主状态写入由调用方在 sessionGuard / 对象同一性判定之后完成
async function load(ctx: AdminCtx, sess: Sess, tab: U360Tab): Promise<void> {
  const path = { id: sess.userId };
  switch (tab) {
    case 'overview': {
      const ov = await ctx.gp.api.call<Overview360>(R360, { path, query: { tab } });
      sess.kyc = ov.kyc_status === 'none' ? null : (await ctx.gp.api.call<KycAdminView>('GET /api/admin/users/{id}/kyc', { path })).latest;
      sess.ov = ov;
      return;
    }
    case 'cards':
      sess.cards = (await ctx.gp.api.call<List360<Card360>>(R360, { path, query: { tab } })).list;
      return;
    case 'funds':
      sess.funds = (await ctx.gp.api.call<List360<Fund360>>(R360, { path, query: { tab } })).list;
      return;
    case 'spend':
      sess.spend = (await ctx.gp.api.call<List360<Spend360>>(R360, { path, query: { tab } })).list;
      return;
    case 'commissions':
      sess.comm = await ctx.gp.api.call<Commissions360>(R360, { path, query: { tab } });
      return;
    case 'log': {
      const p = await ctx.gp.api.call<Log360Page>(R360, { path, query: { tab, page: 1 } });
      sess.logs = { rows: p.list, total: p.total, page: p.page };
      return;
    }
  }
}

function loaded(sess: Sess, tab: U360Tab): boolean {
  switch (tab) {
    case 'overview':
      return sess.ov !== null;
    case 'cards':
      return sess.cards !== null;
    case 'funds':
      return sess.funds !== null;
    case 'spend':
      return sess.spend !== null;
    case 'commissions':
      return sess.comm !== null;
    case 'log':
      return sess.logs !== null;
  }
}

function show(ctx: AdminCtx, sess: Sess, tab: U360Tab): void {
  sess.active = tab;
  const spec = build360(ctx, sess);
  sess.spec = spec;
  openDrawer(ctx, spec);
}

// 用户 360 下钻（shells.openUser360 委派目标）：先拉概览（标题 / 底部按钮依赖），再按需拉目标页签；同一宿主连续打开只认最后一次
export function open360(ctx: AdminCtx, userId: number, tab: U360Tab = 'overview'): void {
  hookSession(ctx);
  const host = ctx.host;
  dispose(sessions.get(host));
  const alive = sessionGuard(ctx);
  const sess: Sess = {
    host, userId, alive, ov: null, kyc: null, cards: null, funds: null, spend: null, comm: null, logs: null, moreBusy: false, active: tab, spec: null, blobs: new Map(), watch: null,
  };
  sessions.set(host, sess);
  const seq = (openSeq.get(host) ?? 0) + 1;
  openSeq.set(host, seq);
  const mine = (): boolean => alive() && openSeq.get(host) === seq;
  void Promise.all([load(ctx, sess, 'overview'), tab === 'overview' ? Promise.resolve() : load(ctx, sess, tab)]).then(
    () => {
      if (mine()) show(ctx, sess, tab);
    },
    (e: unknown) => {
      if (mine()) ctx.gp.ui.toast(ctx.gp.ui.err(e));
    },
  );
}

function pick360(ctx: AdminCtx, sess: Sess, i: number): void {
  const tab = TABS_360[i];
  if (!tab) return;
  show(ctx, sess, tab);
  if (loaded(sess, tab)) return;
  const pending = sess.spec;
  // 迟到结果按对象同一性丢弃：期间切了别的页签 / 关闭 / 换人，tb.drawer 已不是 pending
  void load(ctx, sess, tab).then(
    () => {
      if (sess.alive() && ctx.host.state.tb.drawer === pending) show(ctx, sess, tab);
    },
    (e: unknown) => {
      if (sess.alive() && ctx.host.state.tb.drawer === pending) ctx.gp.ui.toast(ctx.gp.ui.err(e));
    },
  );
}

// 06 C-04 操作记录：每页 20，末行「加载更多」追加
async function loadMore(ctx: AdminCtx, sess: Sess): Promise<void> {
  const cur = sess.logs;
  if (!cur || cur.rows.length >= cur.total || sess.moreBusy) return;
  sess.moreBusy = true;
  const pending = sess.spec;
  try {
    const p = await ctx.gp.api.call<Log360Page>(R360, { path: { id: sess.userId }, query: { tab: 'log', page: cur.page + 1 } });
    if (!sess.alive() || sess.logs !== cur) return;
    sess.logs = { rows: [...cur.rows, ...p.list], total: p.total, page: p.page };
    if (ctx.host.state.tb.drawer === pending) show(ctx, sess, 'log');
  } catch (e) {
    if (sess.alive() && ctx.host.state.tb.drawer === pending) ctx.gp.ui.toast(ctx.gp.ui.err(e));
  } finally {
    sess.moreBusy = false;
  }
}

type Part = 'overview' | 'funds';
type Li = NonNullable<DrawerSpec['list']>['rows'][number];

// 写动作成功后：本会话缓存失效；抽屉仍是本次打开的 360 时重拉概览与当前页签并重绘
async function refresh360(ctx: AdminCtx, userId: number, parts: readonly Part[]): Promise<void> {
  const sess = sessions.get(ctx.host);
  if (!sess || sess.userId !== userId) return;
  for (const p of parts) {
    if (p === 'overview') sess.ov = null;
    if (p === 'funds') sess.funds = null;
  }
  const pending = sess.spec;
  if (pending === null || ctx.host.state.tb.drawer !== pending) return;
  const tab = sess.active;
  try {
    await Promise.all([sess.ov === null ? load(ctx, sess, 'overview') : Promise.resolve(), tab !== 'overview' && !loaded(sess, tab) ? load(ctx, sess, tab) : Promise.resolve()]);
  } catch (e) {
    if (sess.alive() && ctx.host.state.tb.drawer === pending) ctx.gp.ui.toast(ctx.gp.ui.err(e));
    return;
  }
  if (sess.alive() && ctx.host.state.tb.drawer === pending) show(ctx, sess, tab);
}

async function afterWrite(ctx: AdminCtx, userId: number, parts: readonly Part[]): Promise<void> {
  await reloadCurrent(ctx);
  await refresh360(ctx, userId, parts);
}

// KYC 材料只放行图片类型（纵深防御；后端 filestore 已按 magic number 限定 jpg / png / webp 并带 nosniff，07 §7.3）
const KYC_MIME: ReadonlySet<string> = new Set(['image/jpeg', 'image/png', 'image/webp']);
const mimeOf = (b: Blob): string => (b.type.split(';')[0] ?? '').trim().toLowerCase();

// KYC 材料：Bearer 拉取 blob → 新窗打开；同一抽屉会话内复用（06 C-04 [本册定]；偏差 §6-P1）。
// 弹窗拦截：在点击的同步调用栈内先开 about:blank 占位（保留用户激活），取到 blob 后断开 opener 再 location.replace；
// 占位被拦截（open 返回 null）时回落为取到后 noopener 直开；失败 / 会话失效关闭占位窗
function openKycFile(ctx: AdminCtx, sess: Sess, fileId: string): Promise<void> {
  const hit = sess.blobs.get(fileId);
  if (hit !== undefined) {
    globalThis.open(hit, '_blank', 'noopener');
    return Promise.resolve();
  }
  return guarded(`kycfile:${fileId}`, async (c) => {
    // async 函数体在首个 await 之前同步执行：占位窗仍处于点击的用户激活内；在途锁命中时 guarded 不进入本体，不会多开占位
    const w = globalThis.open('about:blank', '_blank');
    try {
      const { blob } = await c.gp.api.blob('GET /api/admin/files/kyc/{id}', { path: { id: fileId } });
      if (!sess.alive() || sessions.get(c.host) !== sess) {
        w?.close();
        return;
      }
      const mime = mimeOf(blob);
      if (!KYC_MIME.has(mime)) throw new Error(`kyc file mime not allowed: ${mime || '(empty)'}`);
      const url = URL.createObjectURL(blob);
      sess.blobs.set(fileId, url);
      watchClose(c, sess);
      if (w) {
        w.opener = null;
        w.location.replace(url);
      } else {
        globalThis.open(url, '_blank', 'noopener');
      }
    } catch (e) {
      w?.close();
      if (!sess.alive()) return;
      console.warn('[gp:admin] kyc file', fileId, e);
      c.gp.ui.toast(str(c.gp.ui.dict(), 'loadFail'));
    }
  })(ctx);
}

async function kycReview(ctx: AdminCtx, userId: number, action: 'approve' | 'reject', note: string | null): Promise<void> {
  const t = ctx.t;
  const n = (note ?? '').trim();
  if (action === 'reject' && (n.length < 1 || [...n].length > NOTE_MAX)) {
    ctx.gp.ui.toast(str(t, 'usrErrNoteReject'));
    return;
  }
  if ([...n].length > NOTE_MAX) {
    ctx.gp.ui.toast(str(t, 'errNoteLong'));
    return;
  }
  const alive = sessionGuard(ctx);
  await ctx.gp.api.call('POST /api/admin/users/{id}/kyc-review', { path: { id: userId }, body: note === null ? { action } : { action, note: n } });
  if (!alive()) return;
  ctx.gp.ui.toast(str(ctx.gp.ui.dict(), action === 'approve' ? 'toastKycApproved' : 'toastRejected'));
  await afterWrite(ctx, userId, ['overview']);
}

async function setBan(ctx: AdminCtx, userId: number, ban: boolean): Promise<void> {
  const alive = sessionGuard(ctx);
  await ctx.gp.api.call(ban ? 'POST /api/admin/users/{id}/ban' : 'POST /api/admin/users/{id}/unban', { path: { id: userId }, body: {} });
  if (!alive()) return;
  ctx.gp.ui.toast(str(ctx.gp.ui.dict(), 'toastBan'));
  await afterWrite(ctx, userId, ['overview']);
}

const banAction = (userId: number, ban: boolean): ((ctx: AdminCtx) => Promise<void>) => guarded(`ban:${userId}`, (c) => setBan(c, userId, ban));
const kycAction = (userId: number, action: 'approve' | 'reject', note: (c: AdminCtx) => string | null): ((ctx: AdminCtx) => Promise<void>) =>
  guarded(`kyc:${userId}`, (c) => kycReview(c, userId, action, note(c)));

// 人工调账三入口（users 行内 / 360 概览与资金页签 / wallets 抽屉）：表单为 funds.adjustForm，成功后刷新当前列表与本次 360 抽屉
function openAdjust(ctx: AdminCtx, user: { id: number; label: string }): void {
  const base = adjustForm(ctx, user);
  openForm(ctx, {
    ...base,
    submit: async (c, v) => {
      const alive = sessionGuard(c);
      await base.submit(c, v);
      if (alive()) void afterWrite(c, user.id, ['overview', 'funds']);
    },
  });
}

// ---- 360 抽屉渲染 ----

function overviewFields(ctx: AdminCtx, t: Dict, ov: Overview360, kyc: KycLatest | null): Array<{ k: string; v: string; wide?: boolean }> {
  const z = tz(ctx);
  const owed = micro(ov.fee_owed);
  const fields: Array<{ k: string; v: string; wide?: boolean }> = [
    { k: 'UID', v: ov.uid },
    { k: str(t, 'cTgId'), v: ov.telegram_id === null ? str(t, 'tgUnbound') : String(ov.telegram_id) },
    { k: str(t, 'cTgName'), v: tgName(ov) || EMPTY },
    { k: str(t, 'cKyc'), v: kycLabel(t, ov.kyc_status) },
    { k: 'USDT', v: amt(ov.balance) },
    { k: 'USD', v: amt(ov.balance) }, // D2：USD 为 1:1 记账镜像
    { k: str(t, 'cFrozenBal'), v: amt(ov.frozen) },
    { k: str(t, 'cFeeOwed'), v: owed !== null && owed > 0n ? usd(ov.fee_owed) : owed === null ? ov.fee_owed : EMPTY },
    { k: str(t, 'cCards'), v: String(ov.card_count) },
    { k: str(t, 'cSpend'), v: usd(ov.spent_total) },
    { k: str(t, 'cAgent'), v: agentText(ov.agent) },
    { k: str(t, 'cLastLogin'), v: fmtDateTime(ov.last_login_at, { tz: z }) },
    { k: str(t, 'uDevice'), v: [ov.last_device, ov.last_login_ip].filter((x): x is string => !!x).join(' · ') || EMPTY },
    { k: str(t, 'cReg'), v: fmtDateTime(ov.created_at, { tz: z }) },
    { k: str(t, 'cTermsAt'), v: fmtDateTime(ov.terms_accepted_at, { tz: z }) },
  ];
  if (kyc) {
    const doc = kyc.doc_type === 'passport' ? str(t, 'kycDocPassport') : kyc.doc_type === 'id' ? str(t, 'kycDocId') : kyc.doc_type;
    fields.push(
      { k: str(t, 'usrKycDocType'), v: doc },
      { k: str(t, 'usrKycName'), v: kyc.full_name },
      { k: str(t, 'usrKycIdNo'), v: kyc.id_number },
      { k: str(t, 'usrKycCountry'), v: kyc.country },
      { k: str(t, 'usrKycSubmitted'), v: fmtDateTime(kyc.created_at, { tz: z }) },
      { k: str(t, 'usrKycStatus'), v: kycLabel(t, kyc.status) },
      { k: str(t, 'usrKycReviewer'), v: kyc.reviewed_by_account ? `${kyc.reviewed_by_account} · ${fmtDateTime(kyc.reviewed_at, { tz: z })}` : EMPTY },
    );
    if (kyc.status === 'rejected') fields.push({ k: str(t, 'usrKycRejectNote'), v: kyc.review_note ?? EMPTY, wide: true });
  }
  return fields;
}

const TIMELINE_LABEL: Readonly<Record<string, string>> = { register: 'Register', first_card: 'First card issued', telegram_linked: 'Telegram linked' }; // 原型 L659 字面（双语同文）

function actorText(t: Dict, kind: string): string {
  return textOr(t, `actor${pascal(kind)}`, kind);
}

function channelText(t: Dict, ch: string): string {
  return textOr(t, `ch${pascal(ch)}`, ch);
}

function actions360(t: Dict, sess: Sess, ov: Overview360): DrawerAction[] {
  if (ov.status === 'merged') return [{ label: str(t, 'aClose'), kind: 'primary', run: (c) => closeDrawer(c) }];
  const adjust: DrawerAction = { label: str(t, 'aAdjust'), kind: 'green', need: U_EDIT, run: (c) => openAdjust(c, { id: ov.id, label: ov.email ?? ov.uid }) };
  if (sess.active === 'funds') return [adjust];
  if (ov.kyc_status === 'pending') {
    if (sess.active !== 'overview') return [adjust];
    const note = (c: AdminCtx): string => c.host.state.tb.note;
    return [
      { label: str(t, 'aReject'), kind: 'danger', need: U_APPROVE, run: kycAction(ov.id, 'reject', note) },
      { label: str(t, 'aApprove'), kind: 'green', need: U_APPROVE, run: kycAction(ov.id, 'approve', note) },
    ];
  }
  const banned = ov.status === 'banned';
  return [{ label: str(t, banned ? 'aUnban' : 'aBan'), kind: banned ? 'primary' : 'danger', need: U_APPROVE, run: banAction(ov.id, !banned) }, adjust];
}

function body360(ctx: AdminCtx, t: Dict, sess: Sess, ov: Overview360): Pick<DrawerSpec, 'fields' | 'list' | 'timeline' | 'note'> {
  const z = tz(ctx);
  switch (sess.active) {
    case 'overview': {
      const kyc = sess.kyc;
      return {
        fields: overviewFields(ctx, t, ov, kyc),
        ...(kyc
          ? {
              list: {
                title: str(t, 'kycDocs'),
                rows: ([['kycFront', kyc.files.front], ['kycBack', kyc.files.back], ['kycSelfie', kyc.files.selfie]] as const)
                  .filter(([, id]) => id !== '')
                  .map(([key, id]) => ({ left: str(t, key), sub: id, right: '↗', go: (c: AdminCtx) => void openKycFile(c, sess, id) })),
              },
            }
          : {}),
        timeline: ov.timeline.map((n) => ({
          label: n.key === 'kyc' ? `KYC ${kycLabel(t, n.status ?? '')}` : TIMELINE_LABEL[n.key] ?? n.key,
          time: fmtDateTime(n.at, { tz: z }),
        })),
        note: ov.kyc_status === 'pending',
      };
    }
    case 'cards': {
      const rows = sess.cards ?? [];
      return {
        list: {
          title: `${str(t, 'uCards')} (${rows.length})`,
          rows: rows.map((c) => ({
            left: c.card_no,
            sub: `${c.product_name} · ${c.status}`,
            right: usd(c.available),
            rightSub: fmtDateTime(c.opened_at, { tz: z }),
            tone: (c.status === 'ACTIVE' ? 'good' : c.status === 'CLOSED' || c.status === 'FAILED' ? 'bad' : 'warn') satisfies Tone,
            go: (cc: AdminCtx) => openCardDrawer(cc, c.id),
          })),
        },
      };
    }
    case 'funds':
      return {
        list: {
          title: str(t, 'uFunds'),
          rows: (sess.funds ?? []).map((f) => {
            const st: { text: string; tone: Tone } =
              f.status === 'pending'
                ? { text: str(t, 'stLedgerPending'), tone: 'warn' }
                : f.status === 'cancel'
                  ? { text: str(t, 'stCancel'), tone: '' }
                  : { text: `${str(t, 'cAfter')} ${amt(f.balance_after)}`, tone: (micro(f.amount) ?? 0n) > 0n ? 'good' : '' };
            return { left: ledgerTitle(t, f.title_tpl, f.title_params), sub: `${f.biz_no} · ${fmtDateTime(f.created_at, { tz: z })}`, right: `${signed(f.amount)} ${f.currency}`, rightSub: st.text, tone: st.tone };
          }),
        },
      };
    case 'spend':
      return {
        list: {
          title: str(t, 'uSpend'),
          rows: (sess.spend ?? []).map((x) => {
            const fee = micro(x.fee_charged);
            return {
              left: x.merchant_name ?? EMPTY,
              sub: `${x.card_no} · ${fmtDateTime(x.transaction_date, { tz: z })}${origSub(x)}${fee !== null && fee > 0n && x.fee_charged !== null ? ` · fee ${usd(x.fee_charged)}` : ''}`,
              right: x.billing_amount === null ? EMPTY : `${x.transaction_type === 'Refund' ? '+' : '-'}${usd(x.billing_amount)}`,
              rightSub: textOr(t, TX_STATUS_KEY[x.status] ?? '', x.status),
              tone: (x.status === 'AuthorizeDeclined' ? 'bad' : x.status === 'Authorize' ? 'warn' : 'good') satisfies Tone,
            };
          }),
        },
      };
    case 'commissions': {
      const cm = sess.comm;
      return {
        fields: cm
          ? [
              { k: str(t, 'uInvite'), v: cm.invite_code || EMPTY },
              { k: str(t, 'uInvited'), v: String(cm.invited) },
              { k: str(t, 'cAgent'), v: agentText(cm.agent) },
              { k: str(t, 'cEarned'), v: usdt(cm.source_total) },
              { k: str(t, 'uCommBal'), v: usdt(cm.commission_balance) },
              { k: str(t, 'uCommEarned'), v: usdt(cm.earned_total) },
            ]
          : [],
        list: {
          title: str(t, 'uCommission'),
          rows: (cm?.list ?? []).map((r) => ({
            left: r.beneficiary.label,
            // 0045：代理受益为加价差价（ck{Kind}：开卡差价 / 充值差价 / 汇率差）；用户受益为用户链拉新奖励（ck{Kind}User：开卡奖励 / 充值奖励）
            sub: `${r.level_label} · ${r.beneficiary.kind === 'user' ? textOr(t, `ck${pascal(r.kind)}User`, textOr(t, `ck${pascal(r.kind)}`, r.kind)) : textOr(t, `ck${pascal(r.kind)}`, r.kind)} · ${fmtDateTime(r.created_at, { tz: z })}`,
            right: `+${usdt(r.amount)}`,
            rightSub: str(t, r.status === 'settled' ? 'stSettled' : 'stUnsettled'),
            tone: (r.status === 'settled' ? 'good' : 'warn') satisfies Tone,
          })),
        },
      };
    }
    case 'log': {
      const lg = sess.logs;
      const opener = logOpener;
      // 行展开需 logs.view（S-20）；未注入 C-23 详情抽屉或无该位时行不可点
      const clickable = opener !== null && can(ctx.s.sh.me, 'logs', 'view');
      const rows = (lg?.rows ?? []).map((r): Li => ({
        left: r.action,
        sub: `${actorText(t, r.actor_kind)}·${r.actor_label}·${channelText(t, r.channel)}`,
        right: fmtDateTime(r.created_at, { tz: z }),
        rightSub: r.result === 'ok' ? str(t, 'stSuccess') : `${str(t, 'stFailed')}${r.error_code === null ? '' : ` ${r.error_code}`}`,
        tone: r.result === 'ok' ? 'good' : 'bad',
        ...(clickable && opener ? { go: (c: AdminCtx) => opener(c, r.id) } : {}),
      }));
      const more: Li[] = lg !== null && lg.rows.length < lg.total ? [{ left: str(t, 'loadMore'), go: (c: AdminCtx) => void loadMore(c, sess) }] : [];
      return { list: { title: str(t, 'uLog'), rows: [...rows, ...more] } };
    }
  }
}

function build360(ctx: AdminCtx, sess: Sess): DrawerSpec {
  const t = ctx.gp.ui.dict();
  const ov = sess.ov;
  if (!ov) return { kind: str(t, 'users'), title: EMPTY, actions: [] };
  const tg = `TG ${ov.telegram_id === null ? str(t, 'tgUnbound') : String(ov.telegram_id)}${ov.telegram_username ? ` (${tgName(ov)})` : ''}`;
  const merged = ov.status === 'merged' && ov.merged_into_uid ? ` · ${interpolate(str(t, 'mergedInto'), { uid: ov.merged_into_uid })}` : '';
  return {
    kind: str(t, 'users'),
    title: ov.email ?? `TG ${ov.telegram_id === null ? str(t, 'tgUnbound') : String(ov.telegram_id)}`,
    subtitle: `${ov.uid} · ${tg}${merged}`,
    tabs: { labels: strArr(t, 'u360'), active: Math.max(0, TABS_360.indexOf(sess.active)), pick: (c, i) => pick360(c, sess, i) },
    ...body360(ctx, t, sess, ov),
    actions: actions360(t, sess, ov),
  };
}

// ---- C-03 编辑用户（formDef user） ----

interface EditBody {
  body: Record<string, string | null>;
  profile: boolean;
  status: boolean;
}

const val = (v: FormVals, k: string): string => {
  const x = v[k];
  return typeof x === 'string' ? x.trim() : '';
};

// 只提交变更字段：键缺省 = 不改，JSON null = 清空（domain/adminuser Field[T]，spec §4.2）
function editBody(u: AdminUserRow, v: FormVals, canStatus: boolean): EditBody {
  const body: Record<string, string | null> = {};
  const put = (k: string, next: string, prev: string): void => {
    if (next !== prev) body[k] = next === '' ? null : next;
  };
  put('email', val(v, 'email'), u.email ?? '');
  put('telegram_id', val(v, 'telegram_id'), u.telegram_id === null ? '' : String(u.telegram_id));
  put('telegram_username', val(v, 'telegram_username').replace(/^@/, ''), u.telegram_username ?? '');
  put('agent_biz_no', val(v, 'agent_biz_no'), u.agent?.biz_no ?? '');
  const profile = Object.keys(body).length > 0;
  const st = val(v, 'status');
  const status = canStatus && st !== '' && st !== u.status;
  if (status) body['status'] = st;
  return { body, profile, status };
}

export function editUserForm(ctx: AdminCtx, u: AdminUserRow, termsAcceptedAt: string | null = null): FormDef {
  const t = ctx.t;
  const route: RouteKey = 'PUT /api/admin/users/{id}';
  // 后端 PUT 改 status 另判 users.approve（07 §4）：无该位时状态只读，本地拦截优先
  const canStatus = can(ctx.s.sh.me, 'users', 'approve');
  const fields: readonly FieldDef[] = [
    { k: 'email', label: str(t, 'cEmail2'), type: 'text', optional: true, placeholder: 'name@mail.com' },
    { k: 'telegram_id', label: str(t, 'cTgId'), type: 'text', optional: true, placeholder: '7100000001' },
    { k: 'telegram_username', label: str(t, 'cTgName'), type: 'text', optional: true, placeholder: '@username' },
    { k: 'kyc', label: str(t, 'cKyc'), type: 'readonly' },
    canStatus ? { k: 'status', label: str(t, 'cStatus'), type: 'select', options: ['active', 'banned'], optionLabel: userStatusLabel } : { k: 'status', label: str(t, 'cStatus'), type: 'readonly' },
    { k: 'terms', label: str(t, 'cTermsAt'), type: 'readonly' },
    { k: 'agent_biz_no', label: str(t, 'cAgent'), type: 'text', optional: true },
  ];
  return {
    title: `${str(t, 'aEdit')} · ${u.email ?? u.uid}`,
    tip: `${str(t, 'userFormTip')}${str(t, 'tgIdTip')}`,
    fields,
    init: {
      email: u.email ?? '',
      telegram_id: u.telegram_id === null ? '' : String(u.telegram_id),
      telegram_username: tgName(u),
      kyc: `${kycLabel(t, u.kyc_status)} · ${str(t, 'kycReadonlyTip')}`,
      status: canStatus ? u.status : userStatusLabel(t, u.status),
      terms: fmtDateTime(termsAcceptedAt, { tz: tz(ctx) }),
      agent_biz_no: u.agent?.biz_no ?? '',
    },
    // 校验 / 提交红条取渲染期字典（表单打开期间切换语言不残留旧文案）
    validate: (v, tt) => {
      const e = editBody(u, v, canStatus);
      if (e.profile && e.status) return str(tt, 'usrErrSplitSave'); // 后端此时 4220 field=status
      const email = val(v, 'email');
      if (email !== '' && !EMAIL_RE.test(email)) return str(tt, 'usrErrEmail');
      const tg = val(v, 'telegram_id');
      if (tg !== '' && !TG_ID_RE.test(tg)) return str(tt, 'usrErrTgId');
      return '';
    },
    submit: async (c, v) => {
      const d = c.gp.ui.dict();
      const e = editBody(u, v, canStatus);
      if (e.profile && e.status) throw localErr(route, str(d, 'usrErrSplitSave'));
      if (!e.profile && !e.status) return;
      const alive = sessionGuard(c);
      try {
        await c.gp.api.call(route, { path: { id: u.id }, body: e.body });
      } catch (err) {
        // X-14：referrer_kind=agent 的用户清空上级代理 → 服务端 4220 field=agent_biz_no（列表出口无 referrer_kind，以服务端判定为准）
        if (err instanceof ApiError && err.code === 4220 && err.data.field === 'agent_biz_no' && e.body['agent_biz_no'] === null) throw localErr(route, str(c.gp.ui.dict(), 'errAgentClear'));
        if (err instanceof ApiError && err.code === 4220 && err.data.field === 'status') throw localErr(route, str(c.gp.ui.dict(), 'usrErrSplitSave'));
        throw err; // 1005 / 1008 由表单红条经 ui.err 映射（err1005 / err1008）；1012（merged 竞态）走通用兜底文案
      }
      if (!alive()) return;
      c.gp.ui.toast(str(c.gp.ui.dict(), 'toastSaved'));
      void afterWrite(c, u.id, ['overview']);
    },
  };
}

// 条款同意时间仅 360 概览出口提供（S-60）：先取概览再开表单
function openEdit(ctx: AdminCtx, u: AdminUserRow): Promise<void> {
  return guarded(`edit:${u.id}`, async (c) => {
    const alive = sessionGuard(c);
    const ov = await c.gp.api.call<Overview360>(R360, { path: { id: u.id }, query: { tab: 'overview' } });
    if (!alive()) return;
    openForm(c, editUserForm(c, u, ov.terms_accepted_at));
  })(ctx);
}

// ---- C-03 列表 ----

function userCells(ctx: AdminCtx, u: AdminUserRow): readonly CellVals[] {
  const t = ctx.t;
  const z = tz(ctx);
  const owed = micro(u.fee_owed);
  const tgSub = u.telegram_id === null ? str(t, 'tgUnbound') : `TG ${u.telegram_id}${u.telegram_username ? ` · ${tgName(u)}` : ''}`;
  const status = chipOf(chipMap(t, USER_STATUS), u.status, 'user.status');
  return [
    cell(u.uid, fmtDateTime(u.created_at, { tz: z }), true),
    u.email === null ? grey(cell(str(t, 'emailUnbound'), tgSub)) : cell(u.email, tgSub),
    chipOf(chipMap(t, KYC_STATUS), u.kyc_status, 'user.kyc_status'),
    cell(agentText(u.agent)),
    cell(`${amt(u.balance)} / ${amt(u.balance)}`), // D2：USD 为 1:1 记账镜像
    owed === null ? cell(u.fee_owed) : owed > 0n ? red(cell(usd(u.fee_owed))) : cell(EMPTY),
    cell(u.card_count),
    cell(usd(u.spent_total)),
    cell(fmtDateTime(u.created_at, { tz: z })),
    u.status === 'merged' && u.merged_into_uid ? { ...status, sub: `→ ${u.merged_into_uid}` } : status,
  ];
}

function userActions(): readonly ActionDef<AdminUserRow>[] {
  const live = (u: AdminUserRow): boolean => u.status !== 'merged';
  return [
    { label: 'aOpen360', run: (c, u) => open360(c, u.id) },
    { label: 'aEdit', need: U_EDIT, show: live, run: (c, u) => openEdit(c, u) },
    { label: 'aApprove', need: U_APPROVE, show: (u) => u.kyc_status === 'pending', run: (c, u) => kycAction(u.id, 'approve', () => null)(c) },
    { label: 'aBan', need: U_APPROVE, danger: true, show: (u) => u.kyc_status !== 'pending' && u.status === 'active', run: (c, u) => banAction(u.id, true)(c) },
    { label: 'aUnban', need: U_APPROVE, show: (u) => u.kyc_status !== 'pending' && u.status === 'banned', run: (c, u) => banAction(u.id, false)(c) },
    { label: 'aAdjust', need: U_EDIT, show: live, run: (c, u) => openAdjust(c, { id: u.id, label: u.email ?? u.uid }) },
  ];
}

function userView(key: (typeof USER_TABS)[number]): ErasedView {
  return defineView<AdminUserRow>({
    key,
    source: { route: 'GET /api/admin/users', tabParam: 'tab' },
    echo: '/admin/users',
    cols: [
      { label: 'cUser' }, { label: 'cEmail' }, { label: 'cKyc', facet: 'kyc_status', facetLabel: kycLabel }, { label: 'cAgent' }, { label: 'cWallet' }, { label: 'cFeeOwed' },
      { label: 'cCards' }, { label: 'cSpend' }, { label: 'cReg', sortKey: 'created_at' }, { label: 'cStatus', facet: 'status', facetLabel: userStatusLabel },
    ],
    cells: userCells,
    open: (c, u) => open360(c, u.id),
    rowActions: () => userActions(),
    note: (t) => str(t, 'usersNote'),
    csvName: 'users',
  });
}

// ---- C-07 用户资金流水 ----

function statsOf(x: unknown): WalletLogStats | null {
  if (typeof x !== 'object' || x === null || !('wallet_sum' in x) || typeof x.wallet_sum !== 'string') return null;
  const s: WalletLogStats = { wallet_sum: x.wallet_sum, deposit_today: '', withdraw_today: '', fee_today: '' };
  if ('card_available_sum' in x && typeof x.card_available_sum === 'string') s.card_available_sum = x.card_available_sum;
  if ('unsettled_commission' in x && typeof x.unsettled_commission === 'string') s.unsettled_commission = x.unsettled_commission;
  return s;
}

// 06 C-07 统计卡：后端口径（B4）；字段缺失 / null → 「—」，不以当前页数据聚合冒充全量
function walletStats(ctx: AdminCtx, page: Readonly<Record<string, unknown>>): readonly StatVals[] {
  const t = ctx.t;
  const s = statsOf(page['stats']);
  return [
    { label: str(t, 'userWalletSum'), value: s ? `${amt(s.wallet_sum)} USDT` : EMPTY, sub: 'USDT', dark: true },
    { label: str(t, 'userCardSum'), value: s?.card_available_sum !== undefined ? usd(s.card_available_sum) : EMPTY, sub: str(t, 'cards') },
    { label: str(t, 'stUnsettled'), value: s?.unsettled_commission !== undefined ? usdt(s.unsettled_commission) : EMPTY, sub: str(t, 'commissions') },
  ];
}

// 「变动前」= balance_before；旧出口缺列时 done 行按 balance_after − amount 派生（BigInt micro），其余「—」
export function balanceBefore(r: Pick<WalletLogRow, 'balance_before' | 'balance_after' | 'amount' | 'status'>): string {
  if (r.balance_before !== undefined) return amt(r.balance_before);
  if (r.status !== 'done') return EMPTY;
  const after = micro(r.balance_after);
  const a = micro(r.amount);
  return after === null || a === null ? EMPTY : fmtAmount(after - a, 2);
}

function openWallet(ctx: AdminCtx, r: WalletLogRow): void {
  const t = ctx.t;
  const z = tz(ctx);
  const userLabel = r.user.email ?? r.user.uid;
  openDrawer(ctx, {
    kind: str(t, 'wallets'),
    title: ledgerTitle(t, r.title_tpl, r.title_params),
    subtitle: userLabel,
    fields: [
      { k: 'ID', v: r.biz_no },
      { k: str(t, 'cKind'), v: kindLabel(t, r.kind) },
      { k: str(t, 'cAmount'), v: `${signed(r.amount)} ${r.currency}` },
      { k: str(t, 'cBefore'), v: balanceBefore(r) },
      { k: str(t, 'cAfter'), v: amt(r.balance_after) },
      { k: str(t, 'cTime'), v: fmtDateTime(r.created_at, { tz: z }) },
      // 偏差 §6-P8：关联单据 / trace_id 为文本，不跳转
      { k: str(t, 'usrRefDoc'), v: r.ref_type && r.ref_id !== null ? `${r.ref_type}:${r.ref_id}` : EMPTY },
      { k: 'trace_id', v: r.trace_id ?? EMPTY, wide: true },
    ],
    actions: [
      { label: str(t, 'aOpen360'), kind: '', run: (c) => open360(c, r.user.id) },
      { label: str(t, 'aAdjust'), kind: 'green', need: U_EDIT, run: (c) => openAdjust(c, { id: r.user.id, label: userLabel }) },
    ],
  });
}

function walletView(key: (typeof WALLET_TABS)[number]): ErasedView {
  return defineView<WalletLogRow>({
    key,
    source: { route: 'GET /api/admin/wallet-logs', tabParam: 'tab' },
    echo: '/admin/wallet-logs',
    cols: [
      { label: 'cUser' }, { label: 'cKind', facet: 'kind', facetLabel: kindLabel }, { label: 'cAmount' }, { label: 'cBefore' }, { label: 'cAfter' },
      { label: 'cTime', sortKey: 'created_at' }, { label: 'cStatus', facet: 'status', facetLabel: ledgerStatusLabel },
    ],
    cells: (c, r) => [
      cell(r.user.email ?? r.user.uid, r.biz_no),
      chipOf(kindMap(c.t), r.kind, 'wallet.kind'),
      cell(`${signed(r.amount)} ${r.currency}`),
      cell(balanceBefore(r)),
      cell(amt(r.balance_after)),
      cell(fmtDateTime(r.created_at, { tz: tz(c) })),
      chipOf(chipMap(c.t, LEDGER_STATUS), r.status, 'wallet.status'),
    ],
    open: openWallet,
    rowActions: () => [{ label: 'aDetail', run: (c, r) => openWallet(c, r) }],
    stats: walletStats,
    csvName: 'wallets',
  });
}

// TD-849：两页共享同一文案包对象（mergePagePacks 按引用去重）
const PACK = { i18n: I18N_USERS, rewrites: REWRITES_USERS } as const;

export const PAGES_USERS: PageMap = {
  users: { page: 'users', ...PACK, tabLabels: (t) => strArr(t, 'tabUsers'), views: USER_TABS.map(userView) },
  wallets: { page: 'wallets', ...PACK, tabLabels: (t) => strArr(t, 'tabWallets'), views: WALLET_TABS.map(walletView) },
};
